import {
	list,
	TagKind,
	TagViewModel,
	topTags
} from "client/Api/Tags/tags";
import { createRemoteDataSource } from "../../Hooks/createRemoteDataSource";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { addHighlightToTags, SuggestedTagModel } from "client/Components";
import { convertSortDirectionToDto, DataSourceSortDirection } from "client/Data/sorting";
import { SortDirection } from "client/Api/common";

export type TagDataSourceFilterState = {
	kind: TagKind;
	selectedTagIds: readonly (number | null)[];
	count: number;
	shouldLoadData?: boolean;
};

export const useSuggestedTagsDataSource = createRemoteDataSource<SuggestedTagModel<number>, TagDataSourceFilterState>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true
	})
	.getHook({
		performRequest: async (options): Promise<readonly SuggestedTagModel<number>[]> => {
			const {
				filterState,
				searchTerm,
				sorting
			} = options;

			const {
				selectedTagIds,
				kind,
				count,
				shouldLoadData = true
			} = filterState;

			if (!shouldLoadData) {
				return Promise.resolve([]);
			}

			let sortDirection: SortDirection;
			if (!sorting || sorting.direction === DataSourceSortDirection.None) {
				sortDirection = SortDirection.Ascending;
			} else {
				if (sorting.propertyName !== "text") {
					throw new Error("Only sorting by text/name is supported");
				}

				sortDirection = convertSortDirectionToDto(sorting.direction);
			}

			const isThereSearchItem: boolean = isNotNilOrEmpty(searchTerm);

			let suggestedTags: SuggestedTagModel<number>[];

			if (isThereSearchItem) {
				suggestedTags = (await list({
					searchTerm,
					count,
					kind,
					idsToExclude: selectedTagIds.filter(isNotNil),
					sortDirection
				}))
					.map(convert);
			} else {
				suggestedTags = (await topTags({
					count,
					kind,
					idsToExclude: selectedTagIds.filter(isNotNil),
				}))
					.map(convert);
			}

			const suggestedTagsWithHighlight: readonly SuggestedTagModel<number>[] = addHighlightToTags<number>(
				suggestedTags,
				searchTerm
			);

			return suggestedTagsWithHighlight;
		}
	});

function convert(tagViewModel: TagViewModel): SuggestedTagModel<number> {
	return {
		text: tagViewModel.name,
		value: tagViewModel.id
	};
}